/**
 * Profile Store
 *
 * Zustand store for profile-related state including sessions, 2FA status,
 * login history, and security events.
 */

import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import {
  UserSession,
  LoginHistoryEntry,
  SecurityEvent,
  SecurityAlert,
  TwoFactorStatus,
} from '@/types/profile';
import { profileApi } from '@/services/api/profileService';

const isNotFoundError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') return false;

  const errorRecord = error as {
    code?: unknown;
    message?: unknown;
    status?: unknown;
    response?: { status?: unknown };
  };

  if (errorRecord.status === 404 || errorRecord.response?.status === 404) {
    return true;
  }

  const code = typeof errorRecord.code === 'string' ? errorRecord.code.toLowerCase() : '';
  const message =
    typeof errorRecord.message === 'string' ? errorRecord.message.toLowerCase() : '';

  return code === 'not_found' || code === 'not-found' || message.includes('not found');
};

interface ProfileState {
  // Sessions
  sessions: UserSession[];
  currentSession: UserSession | null;
  sessionsLoading: boolean;
  sessionsError: string | null;

  // Two-Factor Authentication
  twoFactorStatus: TwoFactorStatus | null;
  twoFactorLoading: boolean;
  twoFactorError: string | null;

  // Login History
  loginHistory: LoginHistoryEntry[];
  loginHistoryLoading: boolean;
  loginHistoryError: string | null;
  loginHistoryTotal: number;
  loginHistoryPage: number;

  // Security Events
  securityEvents: SecurityEvent[];
  securityEventsLoading: boolean;
  securityEventsError: string | null;
  securityEventsTotal: number;
  securityEventsPage: number;

  // Security Alerts
  securityAlerts: SecurityAlert[];
  securityAlertsLoading: boolean;
  securityAlertsError: string | null;
  unreadAlertsCount: number;

  // Actions - Sessions
  fetchSessions: () => Promise<void>;
  fetchCurrentSession: () => Promise<void>;
  terminateSession: (sessionId: string) => Promise<void>;
  terminateAllSessions: () => Promise<void>;

  // Actions - Two-Factor
  fetch2FAStatus: () => Promise<void>;
  setup2FA: () => Promise<{ secret: string; qrCode: string }>;
  enable2FA: (token: string) => Promise<void>;
  disable2FA: (password: string, token: string) => Promise<void>;
  generateBackupCodes: () => Promise<string[]>;

  // Actions - Activity History
  fetchLoginHistory: (page?: number, limit?: number) => Promise<void>;
  fetchSecurityEvents: (page?: number, limit?: number) => Promise<void>;
  fetchSecurityAlerts: () => Promise<void>;

  // Utility Actions
  clearErrors: () => void;
  clearSessionsError: () => void;
  clear2FAError: () => void;
  clearLoginHistoryError: () => void;
  clearSecurityEventsError: () => void;
  clearSecurityAlertsError: () => void;
}

export const useProfileStore = create<ProfileState>()(
  subscribeWithSelector(
    immer((set) => ({
      // Initial state - Sessions
      sessions: [],
      currentSession: null,
      sessionsLoading: false,
      sessionsError: null,

      // Initial state - Two-Factor
      twoFactorStatus: null,
      twoFactorLoading: false,
      twoFactorError: null,

      // Initial state - Login History
      loginHistory: [],
      loginHistoryLoading: false,
      loginHistoryError: null,
      loginHistoryTotal: 0,
      loginHistoryPage: 1,

      // Initial state - Security Events
      securityEvents: [],
      securityEventsLoading: false,
      securityEventsError: null,
      securityEventsTotal: 0,
      securityEventsPage: 1,

      // Initial state - Security Alerts
      securityAlerts: [],
      securityAlertsLoading: false,
      securityAlertsError: null,
      unreadAlertsCount: 0,

      // Actions - Sessions
      fetchSessions: async () => {
        set((state) => {
          state.sessionsLoading = true;
          state.sessionsError = null;
        });

        try {
          const response = await profileApi.sessions.getSessions();

          if (!response.data) {
            throw new Error('Failed to fetch sessions');
          }

          let currentSession: UserSession | null = null;
          try {
            const currentSessionResponse = await profileApi.sessions.getCurrentSession();
            currentSession = currentSessionResponse.data ?? null;
          } catch (error) {
            if (!isNotFoundError(error)) {
              throw error;
            }
          }

          const { sessions } = response.data;
          set((state) => {
            state.sessions = sessions;
            state.currentSession = currentSession;
            state.sessionsLoading = false;
          });
        } catch (error) {
          set((state) => {
            state.sessionsError = error instanceof Error ? error.message : 'Failed to fetch sessions';
            state.sessionsLoading = false;
          });
          throw error;
        }
      },

      fetchCurrentSession: async () => {
        set((state) => {
          state.sessionsLoading = true;
          state.sessionsError = null;
        });

        try {
          const response = await profileApi.sessions.getCurrentSession();

          if (!response.data) {
            throw new Error('Failed to fetch current session');
          }

          const sessionData = response.data;
          set((state) => {
            state.currentSession = sessionData ?? null;
            state.sessionsLoading = false;
          });
        } catch (error) {
          set((state) => {
            state.sessionsError =
              error instanceof Error ? error.message : 'Failed to fetch current session';
            state.sessionsLoading = false;
          });
          throw error;
        }
      },

      terminateSession: async (sessionId: string) => {
        try {
          await profileApi.sessions.terminateSession(sessionId);

          set((state) => {
            state.sessions = state.sessions.filter((s) => s.sessionId !== sessionId);
          });
        } catch (error) {
          set((state) => {
            state.sessionsError =
              error instanceof Error ? error.message : 'Failed to terminate session';
          });
          throw error;
        }
      },

      terminateAllSessions: async () => {
        try {
          await profileApi.sessions.terminateAllSessions();

          set((state) => {
            state.sessions = state.currentSession ? [state.currentSession] : [];
          });
        } catch (error) {
          set((state) => {
            state.sessionsError =
              error instanceof Error ? error.message : 'Failed to terminate sessions';
          });
          throw error;
        }
      },

      // Actions - Two-Factor
      fetch2FAStatus: async () => {
        set((state) => {
          state.twoFactorLoading = true;
          state.twoFactorError = null;
        });

        try {
          const response = await profileApi.twoFactor.getStatus();

          if (!response.data) {
            throw new Error('Failed to fetch 2FA status');
          }

          const statusData = response.data;
          set((state) => {
            state.twoFactorStatus = statusData ?? null;
            state.twoFactorLoading = false;
          });
        } catch (error) {
          set((state) => {
            state.twoFactorError =
              error instanceof Error ? error.message : 'Failed to fetch 2FA status';
            state.twoFactorLoading = false;
          });
          throw error;
        }
      },

      setup2FA: async () => {
        set((state) => {
          state.twoFactorLoading = true;
          state.twoFactorError = null;
        });

        try {
          const response = await profileApi.twoFactor.setup();

          if (!response.data) {
            throw new Error('Failed to setup 2FA');
          }

          set((state) => {
            state.twoFactorLoading = false;
          });

          return response.data;
        } catch (error) {
          set((state) => {
            state.twoFactorError = error instanceof Error ? error.message : 'Failed to setup 2FA';
            state.twoFactorLoading = false;
          });
          throw error;
        }
      },

      enable2FA: async (token: string) => {
        set((state) => {
          state.twoFactorLoading = true;
          state.twoFactorError = null;
        });

        try {
          await profileApi.twoFactor.enable({ token });

          // Refresh 2FA status after enabling
          const statusResponse = await profileApi.twoFactor.getStatus();
          const newStatus = statusResponse.data ?? null;

          set((state) => {
            state.twoFactorStatus = newStatus;
            state.twoFactorLoading = false;
          });
        } catch (error) {
          set((state) => {
            state.twoFactorError = error instanceof Error ? error.message : 'Failed to enable 2FA';
            state.twoFactorLoading = false;
          });
          throw error;
        }
      },

      disable2FA: async (password: string, token: string) => {
        set((state) => {
          state.twoFactorLoading = true;
          state.twoFactorError = null;
        });

        try {
          await profileApi.twoFactor.disable({ password, token });

          // Refresh 2FA status after disabling
          const statusResponse = await profileApi.twoFactor.getStatus();
          const newStatus = statusResponse.data ?? null;

          set((state) => {
            state.twoFactorStatus = newStatus;
            state.twoFactorLoading = false;
          });
        } catch (error) {
          set((state) => {
            state.twoFactorError = error instanceof Error ? error.message : 'Failed to disable 2FA';
            state.twoFactorLoading = false;
          });
          throw error;
        }
      },

      generateBackupCodes: async () => {
        set((state) => {
          state.twoFactorLoading = true;
          state.twoFactorError = null;
        });

        try {
          const response = await profileApi.twoFactor.generateBackupCodes();

          if (!response.data) {
            throw new Error('Failed to generate backup codes');
          }

          const backupCodesData = response.data.backupCodes;

          // Refresh 2FA status after generating codes
          const statusResponse = await profileApi.twoFactor.getStatus();
          const newStatus = statusResponse.data ?? null;

          set((state) => {
            state.twoFactorStatus = newStatus;
            state.twoFactorLoading = false;
          });

          return backupCodesData;
        } catch (error) {
          set((state) => {
            state.twoFactorError =
              error instanceof Error ? error.message : 'Failed to generate backup codes';
            state.twoFactorLoading = false;
          });
          throw error;
        }
      },

      // Actions - Activity History
      fetchLoginHistory: async (page = 1, limit = 20) => {
        set((state) => {
          state.loginHistoryLoading = true;
          state.loginHistoryError = null;
        });

        try {
          const response = await profileApi.activity.getLoginHistory({ page, limit });

          if (!response.data) {
            throw new Error('Failed to fetch login history');
          }

          const { data: entries, total } = response.data;
          set((state) => {
            state.loginHistory = entries;
            state.loginHistoryTotal = total;
            state.loginHistoryPage = page;
            state.loginHistoryLoading = false;
          });
        } catch (error) {
          set((state) => {
            state.loginHistoryError =
              error instanceof Error ? error.message : 'Failed to fetch login history';
            state.loginHistoryLoading = false;
          });
          throw error;
        }
      },

      fetchSecurityEvents: async (page = 1, limit = 20) => {
        set((state) => {
          state.securityEventsLoading = true;
          state.securityEventsError = null;
        });

        try {
          const response = await profileApi.activity.getSecurityEvents({ page, limit });

          if (!response.data) {
            throw new Error('Failed to fetch security events');
          }

          const { data: events, total } = response.data;
          set((state) => {
            state.securityEvents = events;
            state.securityEventsTotal = total;
            state.securityEventsPage = page;
            state.securityEventsLoading = false;
          });
        } catch (error) {
          set((state) => {
            state.securityEventsError =
              error instanceof Error ? error.message : 'Failed to fetch security events';
            state.securityEventsLoading = false;
          });
          throw error;
        }
      },

      fetchSecurityAlerts: async () => {
        set((state) => {
          state.securityAlertsLoading = true;
          state.securityAlertsError = null;
        });

        try {
          const response = await profileApi.activity.getSecurityAlerts();

          if (!response.data) {
            throw new Error('Failed to fetch security alerts');
          }

          const { alerts, unreadCount } = response.data;
          set((state) => {
            state.securityAlerts = alerts;
            state.unreadAlertsCount = unreadCount;
            state.securityAlertsLoading = false;
          });
        } catch (error) {
          set((state) => {
            state.securityAlertsError =
              error instanceof Error ? error.message : 'Failed to fetch security alerts';
            state.securityAlertsLoading = false;
          });
          throw error;
        }
      },

      // Utility Actions
      clearErrors: () => {
        set((state) => {
          state.sessionsError = null;
          state.twoFactorError = null;
          state.loginHistoryError = null;
          state.securityEventsError = null;
          state.securityAlertsError = null;
        });
      },

      clearSessionsError: () => {
        set((state) => {
          state.sessionsError = null;
        });
      },

      clear2FAError: () => {
        set((state) => {
          state.twoFactorError = null;
        });
      },

      clearLoginHistoryError: () => {
        set((state) => {
          state.loginHistoryError = null;
        });
      },

      clearSecurityEventsError: () => {
        set((state) => {
          state.securityEventsError = null;
        });
      },

      clearSecurityAlertsError: () => {
        set((state) => {
          state.securityAlertsError = null;
        });
      },
    }))
  )
);

// Selectors for common use cases
export const useProfileSessions = () => {
  const { sessions, currentSession, sessionsLoading, sessionsError } = useProfileStore();
  return { sessions, currentSession, sessionsLoading, sessionsError };
};

export const useProfile2FA = () => {
  const { twoFactorStatus, twoFactorLoading, twoFactorError } = useProfileStore();
  return { twoFactorStatus, twoFactorLoading, twoFactorError };
};

export const useProfileLoginHistory = () => {
  const { loginHistory, loginHistoryLoading, loginHistoryError, loginHistoryTotal } =
    useProfileStore();
  return { loginHistory, loginHistoryLoading, loginHistoryError, loginHistoryTotal };
};

export const useProfileSecurityEvents = () => {
  const { securityEvents, securityEventsLoading, securityEventsError, securityEventsTotal } =
    useProfileStore();
  return { securityEvents, securityEventsLoading, securityEventsError, securityEventsTotal };
};

export const useProfileSecurityAlerts = () => {
  const { securityAlerts, securityAlertsLoading, securityAlertsError, unreadAlertsCount } =
    useProfileStore();
  return { securityAlerts, securityAlertsLoading, securityAlertsError, unreadAlertsCount };
};

// Reset on logout to prevent sensitive data leakage between sessions
if (typeof window !== 'undefined') {
  import('../auth/auth-store').then(({ useAuthStore }) => {
    useAuthStore.subscribe(
      (state) => state.isAuthenticated,
      (isAuthenticated, prev) => {
        if (prev && !isAuthenticated) {
          useProfileStore.setState({
            sessions: [], currentSession: null, sessionsLoading: false, sessionsError: null,
            twoFactorStatus: null, twoFactorLoading: false, twoFactorError: null,
            loginHistory: [], loginHistoryLoading: false, loginHistoryError: null, loginHistoryTotal: 0, loginHistoryPage: 1,
            securityEvents: [], securityEventsLoading: false, securityEventsError: null, securityEventsTotal: 0, securityEventsPage: 1,
            securityAlerts: [], securityAlertsLoading: false, securityAlertsError: null, unreadAlertsCount: 0,
          });
        }
      }
    );
  });
}
